import React, { createContext, useContext, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { UserContext } from '@/components/App';
import { useClientUser } from '@/hooks';
import {
    GET_CORP_DOCUMENTS,
    GET_CORP_INTEGRATION_OVERRIDES,
    GET_CORP_PRICING_OVERRIDES,
    GET_CORP_TARIFFS,
    GET_ORG_PROFILE_ASSIGNMENTS,
    GET_ORG_CONTRACTOR_PARTNERSHIPS,
} from './graphql';
import { buildCarrierAssetMaps, buildContractorRows, isOrgFlexCenter, MARKETPLACE_KEY } from './utils';

// Re-exported for the modals/index that import MARKETPLACE_KEY from this store.
export { MARKETPLACE_KEY };

export const CorpAdminContext = createContext({});

const CorpAdminStore = ({ children }) => {
    const { admin_clients } = useContext(UserContext);
    const { organization_id } = useClientUser();

    const orgClientIds = useMemo(() => new Set((admin_clients || []).map((c) => c.client_id)), [admin_clients]);

    const orgShipperIds = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']).map((c) => c.client_id),
        [admin_clients]
    );

    const allOrgClientIds = useMemo(() => [...orgClientIds], [orgClientIds]);

    const { data: integrationData, loading: intLoading } = useQuery(GET_CORP_INTEGRATION_OVERRIDES, {
        variables: { orgShipperIds },
        skip: orgShipperIds.length === 0,
    });

    const { data: pricingData, loading: priceLoading } = useQuery(GET_CORP_PRICING_OVERRIDES, {
        variables: { orgShipperIds },
        skip: orgShipperIds.length === 0,
    });

    const { data: tariffsData, loading: tariffsLoading } = useQuery(GET_CORP_TARIFFS, {
        variables: { client_ids: allOrgClientIds, organization_id },
        skip: allOrgClientIds.length === 0,
    });

    const { assetHeavy, assetLight } = useMemo(
        () =>
            buildCarrierAssetMaps({
                integrationOverrides: integrationData?.integration_overrides,
                pricingOverrides: pricingData?.pricing_overrides,
                adminClients: admin_clients,
            }),
        [integrationData, pricingData, admin_clients]
    );

    const orgCarrierIds = useMemo(() => {
        const ids = new Set([...Object.keys(assetHeavy), ...Object.keys(assetLight)]);
        ids.delete(MARKETPLACE_KEY);
        return [...ids];
    }, [assetHeavy, assetLight]);

    const orgFlexCenterIds = useMemo(
        () => (admin_clients || []).filter(isOrgFlexCenter).map((c) => c.client_id),
        [admin_clients]
    );

    const { data: contractorData, loading: contractorsLoading } = useQuery(GET_ORG_CONTRACTOR_PARTNERSHIPS, {
        variables: { orgFlexCenterIds },
        skip: orgFlexCenterIds.length === 0,
    });

    const contractors = useMemo(() => buildContractorRows(contractorData?.partnerships), [contractorData]);

    const {
        data: documentsData,
        loading: docsLoading,
        refetch: refetchCorpDocs,
    } = useQuery(GET_CORP_DOCUMENTS, {
        variables: { orgCarrierIds, orgShipperIds, organization_id },
        skip: !organization_id || (orgCarrierIds.length === 0 && orgShipperIds.length === 0),
    });

    const { corpDocsByCarrier, rateAddendumsByCarrierShipper } = useMemo(() => {
        const orgDocs = documentsData?.org_documents || [];
        const rates = documentsData?.rate_addendums || [];

        const corpDocsByCarrier = {};
        for (const doc of orgDocs) {
            if (!doc.client_id) continue;
            corpDocsByCarrier[doc.client_id] ??= {};
            // Only one active doc per (carrier, type) by design — last write wins if duplicate.
            corpDocsByCarrier[doc.client_id][doc.type] = doc;
        }

        const rateAddendumsByCarrierShipper = {};
        for (const doc of rates) {
            if (!doc.client_id || !doc.partner_client_id) continue;
            rateAddendumsByCarrierShipper[doc.client_id] ??= {};
            rateAddendumsByCarrierShipper[doc.client_id][doc.partner_client_id] = doc;
        }

        return { corpDocsByCarrier, rateAddendumsByCarrierShipper };
    }, [documentsData]);

    const { data: assignmentsData } = useQuery(GET_ORG_PROFILE_ASSIGNMENTS, {
        variables: { organization_id },
        skip: !organization_id,
        fetchPolicy: 'cache-and-network',
        onError: (e) => {
            console.error(e);
            Sentry.captureException(e);
        },
    });

    const assignmentsByShipper = useMemo(() => {
        const profiles = assignmentsData?.delivery_profiles || [];
        const result = {};
        profiles.forEach((profile) => {
            const mappedCarrierIds = new Set();
            (profile.subregions || []).forEach((s) => {
                (s.carriers || []).forEach((m) => {
                    if (m.carrier_id) mappedCarrierIds.add(m.carrier_id);
                    if (m.middle_mile_carrier_id) mappedCarrierIds.add(m.middle_mile_carrier_id);
                    if (m.warehouse_carrier_id) mappedCarrierIds.add(m.warehouse_carrier_id);
                });
            });
            (profile.auto_assignments || []).forEach((assignment) => {
                result[assignment.shipper_id] = {
                    profileName: profile.name,
                    mappedCarrierIds,
                };
            });
        });
        return result;
    }, [assignmentsData]);

    const tariffs = tariffsData?.tariffs || [];
    const loading = intLoading || priceLoading || tariffsLoading || docsLoading || contractorsLoading;

    const [notification, setNotification] = useState({});

    return (
        <CorpAdminContext.Provider
            value={{
                state: {
                    assetHeavy,
                    assetLight,
                    contractors,
                    integrationOverrides: integrationData?.integration_overrides || [],
                    orgShipperIds,
                    tariffs,
                    loading,
                    admin_clients,
                    notification,
                    corpDocsByCarrier,
                    rateAddendumsByCarrierShipper,
                    assignmentsByShipper,
                },
                callbacks: { setNotification, refetchCorpDocs },
            }}
        >
            {children}
        </CorpAdminContext.Provider>
    );
};

export default CorpAdminStore;
